import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setTokenValid(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Contraseña actualizada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Volver al inicio</span>
        </button>

        {/* Card */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Restablecer Contraseña</h1>
                <p className="text-orange-100 text-sm">Exam Ready English</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {!token ? (
              // No token provided
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Enlace inválido</h2>
                <p className="text-slate-600 mb-6">No se ha proporcionado un token de recuperación.</p>
                <Button onClick={() => navigate('/auth')} className="rounded-full">
                  Ir a Iniciar Sesión
                </Button>
              </div>
            ) : !tokenValid ? (
              // Invalid or expired token
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Enlace expirado</h2>
                <p className="text-slate-600 mb-6">
                  Este enlace ha expirado o no es válido.<br />
                  Por favor, solicita uno nuevo.
                </p>
                <Button onClick={() => navigate('/forgot-password')} className="rounded-full">
                  Solicitar nuevo enlace
                </Button>
              </div>
            ) : success ? (
              // Success
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h2 className="text-lg font-semibold text-slate-900 mb-2">¡Contraseña actualizada!</h2>
                <p className="text-slate-600 mb-6">Ya puedes iniciar sesión con tu nueva contraseña.</p>
                <Button onClick={() => navigate('/auth')} className="rounded-full bg-green-600 hover:bg-green-500">
                  Ir a Iniciar Sesión
                </Button>
              </div>
            ) : (
              // Reset form
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-slate-600 text-sm mb-4">
                  Introduce tu nueva contraseña. Debe tener al menos 6 caracteres.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirmar contraseña
                  </label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={resetting}
                  className="w-full bg-orange-500 hover:bg-orange-400 rounded-full"
                >
                  {resetting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Actualizando...
                    </>
                  ) : (
                    'Restablecer Contraseña'
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
